import { AxiosResponse } from 'axios';
import { apiClient } from '../../apiHelper';
import {
  useCreate,
  useEdit,
  useSingleFetch,
} from '../react-query';
import organizationQueryKeys from './organization-query-keys';

export interface Organization {
  id: string;
  name: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  logo_url?: string;
  contact_person_name?: string;
  contact_person_email?: string;
  contact_person_phone?: string;
  timezone?: string;
  country?: string;
  city?: string;
  postal_code?: string;
  is_active: boolean;
  is_deleted: boolean;
  force_2fa_enabled: boolean;
  force_2fa_enabled_at?: string;
  created_at: string;
  updated_at?: string;
}

export interface OrganizationCreate {
  name: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  logo_url?: string;
  contact_person_name?: string;
  contact_person_email?: string;
  contact_person_phone?: string;
  timezone?: string;
  country?: string;
  city?: string;
  postal_code?: string;
  is_active?: boolean;
}

export interface OrganizationUpdate {
  name?: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  logo_url?: string;
  contact_person_name?: string;
  contact_person_email?: string;
  contact_person_phone?: string;
  timezone?: string;
  country?: string;
  city?: string;
  postal_code?: string;
  is_active?: boolean;
  force_2fa_enabled?: boolean;
}

export interface OwnershipTransferRequest {
  new_owner_user_id: string;
}

export interface OwnershipTransferConfirmRequest {
  new_owner_user_id: string;
  verification_code: string;
  totp_code?: string;
}

export function useOrganization(config = {}) {
  const getOrganizationFn = async (): Promise<Organization> => {
    const response = await apiClient.get(`organization/`);
    // Handle the new success_response format
    return response.data || response;
  };

  return useSingleFetch(
    getOrganizationFn,
    organizationQueryKeys.detail(),
    'organization',
    config
  );
}


export function useOrganizationUpdate(onSuccess?: (data: any) => void, onError?: (error: any) => void) {
  const updateOrganizationFn = async (data: OrganizationUpdate): Promise<Organization> => {
    const response = await apiClient.put(`organization/`, data);
    // Handle the new success_response format
    return response.data || response;
  };

  return useEdit(
    updateOrganizationFn,
    organizationQueryKeys.all,
    'Error while updating organization',
    'Organization updated successfully',
    onSuccess,
    onError,
    false // disable default notification since we handle it in the component
  );
}

export function useInitiateOwnershipTransfer(
  onSuccess?: (response: any, data: OwnershipTransferRequest) => void,
  onError?: (error: any, data: OwnershipTransferRequest) => void,
  notification = true
) {
  const initiateFn = async (data: OwnershipTransferRequest) => {
    const response = await apiClient.post('organization/transfer-ownership/initiate', data);
    return response;
  };

  return useCreate(
    initiateFn,
    organizationQueryKeys.all,
    'Error while initiating ownership transfer',
    'Verification code sent to your email',
    onSuccess,
    onError,
    notification
  );
}

export function useConfirmOwnershipTransfer(
  onSuccess?: (response: any, data: OwnershipTransferConfirmRequest) => void,
  onError?: (error: any, data: OwnershipTransferConfirmRequest) => void,
  notification = true
) {
  const confirmFn = async (data: OwnershipTransferConfirmRequest) => {
    const response = await apiClient.post('organization/transfer-ownership/confirm', data);
    return response;
  };

  return useCreate(
    confirmFn,
    organizationQueryKeys.all,
    'Error while confirming ownership transfer',
    'Ownership transferred successfully',
    onSuccess,
    onError,
    notification
  );
}
